import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Search, Star, X } from "lucide-react";
import { ROLE_ORDER, ROLE_LABEL, TONE, initials, norm, personColor, peopleWord, absenceLabel, absenceTone } from "@/lib/taskCalendar";
import { Button } from "@/components/ui/button";

export function PersonDot({ person, size = 28 }) {
  return (
    <span className="rounded-full text-white font-extrabold grid place-items-center shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34), background: personColor(person?.id) }}>
      {initials(person?.name)}
    </span>
  );
}

const AVAIL_CLS = { free: "bg-success/15 text-success", busy: "bg-destructive/15 text-destructive", muted: "bg-secondary text-muted-foreground" };
export const pillCls = (tone) => AVAIL_CLS[tone] || TONE[tone]?.tag || "bg-secondary text-muted-foreground";
const Box = ({ state }) => (
  <span className={`w-[18px] h-[18px] shrink-0 rounded-[5px] border-[1.5px] grid place-items-center text-white text-[11px] font-extrabold ${state ? "bg-brand border-brand" : "bg-card border-border"}`}>
    {state === "all" ? <Check size={12} strokeWidth={3} /> : state === "some" ? "–" : null}
  </span>
);

/**
 * Jeden výběr lidí pro dvě místa (z návrhu Design, 191):
 *  mode="filter" — lišta filtrů Úkolů; panel vypadá a otevírá se stejně jako „Filtry“ (karta pod lištou, na mobilu šuplík)
 *  mode="assign" — „Komu to zadáváš“ ve formuláři úkolu (panel s dostupností u každého)
 * part="trigger" = jen tlačítko (do řádku s Filtry), part="panel" = karta výběru, part="tokens" = řádek VYBRÁNO.
 * Ve filtru se otevření řídí zvenku (open / onOpenChange), ať je otevřený vždy jen jeden panel.
 * Prázdný výběr ve filtru = „Všichni lidé“. V assign je první vybraný ten, kdo odpovídá.
 */
export function PeopleFilter({ mode = "filter", people, value, onChange, selections = [], onCreateSelection, onDeleteSelection,
  meId, absentToday = () => null, availability, hint, part, open: openProp, onOpenChange, mobile, applyLabel }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (v) => { const nv = typeof v === "function" ? v(open) : v; if (onOpenChange) onOpenChange(nv); if (openProp === undefined) setOpenState(nv); };
  const cardRef = useRef(null);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(["WORKER", "SUBCONTRACTOR"]);
  const [naming, setNaming] = useState(null);
  const sel = useMemo(() => new Set(value), [value]);
  const filter = mode === "filter";
  const all = !value.length;
  const hit = (p) => !q || norm(`${p.name} ${ROLE_LABEL[p.role] || ""}`).includes(norm(q));
  const same = (ids) => ids.length === value.length && ids.every((i) => sel.has(i));
  const toggle = (id) => onChange(sel.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  const toggleGroup = (ids) => { const every = ids.every((i) => sel.has(i)); onChange(every ? value.filter((x) => !ids.includes(x)) : [...new Set([...value, ...ids])]); };
  const applySel = (s) => (filter ? onChange(same(s.user_ids) ? [] : s.user_ids) : toggleGroup(s.user_ids.filter((i) => people.some((p) => p.id === i))));
  const selected = value.map((id) => people.find((p) => p.id === id)).filter(Boolean);
  const stackSrc = all && filter ? people : selected;
  const inWork = people.filter((p) => !absentToday(p)).map((p) => p.id);
  const away = people.filter((p) => absentToday(p)).map((p) => p.id);
  const canSave = !!onCreateSelection && value.length > 1 && !selections.some((s) => same(s.user_ids)) && naming === null;
  const groups = ROLE_ORDER.map((role) => {
    const mem = people.filter((p) => p.role === role);
    const shown = mem.filter(hit);
    if (!shown.length) return null;
    const k = mem.filter((p) => sel.has(p.id)).length;
    let rows = shown.map((p) => ({ p, a: availability ? availability(p) : null, abs: absentToday(p) }));
    if (availability) rows = rows.sort((x, y) => x.a.rank - y.a.rank);
    return { role, mem, rows, k, open: !!q || !collapsed.includes(role) };
  }).filter(Boolean);
  const label = all ? (filter ? "Všichni lidé" : "Vyber lidi") : selected.length === 1 ? selected[0].name : `${selected.length} ${peopleWord(selected.length)}`;
  const saveNow = async () => { await onCreateSelection?.((naming || "").trim() || `Můj výběr ${selections.length + 1}`, value); setNaming(null); };
  const close = () => { setOpen(false); setQ(""); };
  // Karta ve filtru se zavírá klikem mimo / Escape — stejně jako panel Filtry (desktop).
  useEffect(() => {
    if (!filter || !open || mobile || part !== "panel") return undefined;
    const onDown = (e) => { if (cardRef.current && !cardRef.current.contains(e.target) && !e.target.closest?.("[data-testid=filter-people-trigger]")) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [filter, open, mobile, part]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Panel ve filtru: stejný jazyk jako FilterPanel (pilulky, bílá karta se seznamem, patička)
  const Head = ({ children }) => <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground mr-1">{children}</span>;
  const pill = (on) => `h-9 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${on ? "bg-brand text-white font-bold" : "bg-card border border-border text-foreground"}`;
  const Check4 = ({ state }) => (
    <span className={`h-4 w-4 rounded-[5px] border grid place-items-center shrink-0 text-[11px] font-bold ${state ? "bg-brand border-brand text-white" : "border-border"}`}>
      {state === "all" ? <Check size={12} /> : state === "some" ? "–" : null}
    </span>
  );
  const filterBody = (
    <>
      <div className="flex items-center gap-2 flex-wrap px-[18px] py-3">
        <Head>Lidé</Head>
        {[["Všichni", null], ["Jen já", [meId]], ["Dnes v práci", inWork], ["Dnes chybí", away]].map(([l, ids]) => {
          const on = ids ? ids.length > 0 && same(ids) : all;
          const empty = ids && !ids.length;   // např. dnes nikdo nechybí
          return (
            <button key={l} type="button" disabled={empty} onClick={() => onChange(ids || [])} className={`${pill(on)} px-3.5 disabled:opacity-50`} data-testid={`pp-quick-${l}`}>
              {l}<span className={`tabular-nums ${on ? "" : "text-muted-foreground"}`}>{ids ? ids.length : people.length}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 flex-wrap px-[18px] pb-3">
        <Head>Moje výběry</Head>
        {selections.map((x) => {
          const on = same(x.user_ids);
          return (
            <span key={x.id} className={`${pill(on)} overflow-hidden`}>
              <button type="button" onClick={() => applySel(x)} className="h-full pl-3.5 pr-1 inline-flex items-center gap-1.5" data-testid="saved-selection">
                <Star size={13} />{x.name}<span className={`tabular-nums ${on ? "" : "text-muted-foreground"}`}>{x.user_ids.length}</span>
              </button>
              {onDeleteSelection && <button type="button" onClick={() => onDeleteSelection(x.id)} title="Smazat výběr" aria-label={`Smazat výběr ${x.name}`} className="w-8 h-full grid place-items-center opacity-60 hover:opacity-100"><X size={14} /></button>}
            </span>
          );
        })}
        {!selections.length && naming === null && <span className="text-[13px] text-muted-foreground">Zatím žádný — vyber lidi a ulož si je.</span>}
        {canSave && <button type="button" onClick={() => setNaming("")} className="h-9 px-3.5 rounded-full border border-dashed border-border text-muted-foreground text-sm font-semibold" data-testid="save-selection">+ Uložit výběr</button>}
        {naming !== null && (
          <div className="basis-full flex gap-1.5">
            <input autoFocus value={naming} onChange={(e) => setNaming(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveNow(); if (e.key === "Escape") setNaming(null); }}
              placeholder="Název, např. Parta Dvořák" className="flex-1 min-w-0 h-9 px-3 border border-border rounded-[10px] bg-card text-sm outline-none" data-testid="selection-name" />
            <button type="button" onClick={saveNow} className="h-9 px-3.5 rounded-full bg-brand text-white text-sm font-bold">Uložit</button>
            <button type="button" onClick={() => setNaming(null)} className="h-9 px-2.5 rounded-full text-muted-foreground text-sm font-semibold">Zrušit</button>
          </div>
        )}
      </div>
      <div className="px-[18px] pb-1">
        <div className="bg-card border border-border rounded-[16px] overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
            <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground">Tým</span>
            <span className="text-xs text-muted-foreground/70 tabular-nums">{all ? `${people.length} lidí` : `vybráno ${value.length} z ${people.length}`}</span>
          </div>
          <div className="p-2 border-b border-border/60">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno nebo pozici…" data-testid="people-search"
              className="h-9 w-full bg-secondary rounded-[10px] px-3 text-sm focus:outline-none" />
          </div>
          <div className={`${mobile ? "max-h-[40vh]" : "max-h-[300px]"} overflow-y-auto p-1.5`}>
            {groups.map((g) => (
              <div key={g.role}>
                <div className="flex items-center gap-2 min-h-9 px-2.5">
                  <button type="button" onClick={() => toggleGroup(g.mem.map((p) => p.id))} aria-label={`Vybrat všechny: ${ROLE_LABEL[g.role] || g.role}`}><Check4 state={g.k === g.mem.length ? "all" : g.k ? "some" : null} /></button>
                  <button type="button" onClick={() => setCollapsed((c) => (c.includes(g.role) ? c.filter((x) => x !== g.role) : [...c, g.role]))} className="flex-1 flex items-center gap-2 text-left min-h-9">
                    <span className="flex-1 font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground">{ROLE_LABEL[g.role] || g.role}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">{g.k ? `${g.k} z ${g.mem.length}` : g.mem.length}</span>
                    {g.open ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                  </button>
                </div>
                {g.open && g.rows.map(({ p, abs }) => {
                  const on = sel.has(p.id);
                  return (
                    <button type="button" key={p.id} onClick={() => toggle(p.id)} data-testid={`person-row-${p.id}`}
                      className={`w-full min-h-[42px] px-2.5 rounded-[10px] text-[14.5px] text-left inline-flex items-center gap-2 transition-colors ${on ? "bg-brand/10 font-semibold" : "hover:bg-secondary"}`}>
                      <Check4 state={on ? "all" : null} />
                      <PersonDot person={p} size={26} />
                      <span className="truncate">{p.name}{p.id === meId ? " (já)" : ""} <span className="text-[12.5px] font-normal text-muted-foreground">· {ROLE_LABEL[p.role] || p.role}</span></span>
                      {abs && <span className={`ml-auto shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${TONE[absenceTone(abs)]?.tag}`}>{absenceLabel(abs).toLowerCase()}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
            {!!q && !people.some(hit) && <p className="px-2.5 py-3 text-sm text-muted-foreground">Nikdo takový v týmu není.</p>}
          </div>
        </div>
      </div>
    </>
  );
  const filterFooter = (
    <div className={`flex items-center gap-2.5 px-[18px] py-3.5 border-t border-border/80 ${mobile ? "pb-[max(60px,calc(0.75rem+env(safe-area-inset-bottom)))]" : ""}`}>
      <button type="button" onClick={all ? undefined : () => onChange([])} className={`font-semibold ${all ? "text-muted-foreground/70" : "text-brand"}`} data-testid="pp-clear">Vymazat výběr</button>
      <button type="button" onClick={close} className="ml-auto h-11 px-5 bg-brand text-white rounded-[14px] font-bold" data-testid="pp-apply">{applyLabel || "Hotovo"}</button>
    </div>
  );
  const filterPanel = mobile ? (
    <>
      <div onClick={close} className={`fixed inset-0 z-40 bg-black/45 transition-opacity motion-reduce:transition-none ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      <div data-testid="filter-people-panel"
        className={`fixed inset-x-0 bottom-0 z-50 bg-background rounded-t-[24px] max-h-[88%] overflow-y-auto transition-transform duration-300 motion-reduce:transition-none [transition-timing-function:cubic-bezier(.32,.72,0,1)] ${open ? "translate-y-0" : "translate-y-[104%]"}`}>
        <div className="pt-3">{filterBody}{filterFooter}</div>
      </div>
    </>
  ) : open ? (
    <div ref={cardRef} data-testid="filter-people-panel" style={{ width: 660 }}
      className="mt-2 max-w-full bg-background border border-border rounded-[20px] shadow-[0_22px_50px_rgba(0,0,0,.18)] origin-top animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="pt-2">{filterBody}{filterFooter}</div>
    </div>
  ) : null;

  const panel = (
    <div className={`bg-card border border-border rounded-[16px] overflow-hidden flex flex-col shadow-[0_16px_48px_rgba(0,0,0,.14)] ${filter ? "absolute top-[50px] left-0 z-50 w-[400px] max-w-[calc(100vw-32px)]" : "mt-2"}`} data-testid={`${mode}-people-panel`}>
      <div className="p-3 flex flex-col gap-2.5">
        <div className="flex items-center gap-2 h-10 px-3 border border-border rounded-[12px]">
          <Search size={15} className="text-muted-foreground" />
          <input autoFocus={filter} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledej jméno nebo pozici" data-testid="people-search"
            className="flex-1 min-w-0 bg-transparent outline-none text-sm font-medium" />
        </div>
        {filter && (
          <div className="flex gap-1.5 flex-wrap">
            {[["Všichni", []], ["Jen já", [meId]], ["Dnes v práci", inWork], ["Dnes chybí", away]].map(([l, ids]) => {
              const on = ids.length ? same(ids) : all;
              return <button key={l} type="button" onClick={() => onChange(ids)} className={`h-[30px] px-[11px] rounded-full border text-[12.5px] font-semibold flex items-center gap-1.5 ${on ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border"}`}>{l}<span className="opacity-60 font-medium">{ids.length || people.length}</span></button>;
            })}
          </div>
        )}
        {(filter || selections.length > 0) && (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] font-bold tracking-[.12em] text-muted-foreground mr-0.5">MOJE VÝBĚRY</span>
              {selections.map((s) => {
                const on = filter ? same(s.user_ids) : s.user_ids.every((i) => sel.has(i));
                return (
                  <span key={s.id} className={`flex items-center h-[30px] rounded-full border overflow-hidden ${on ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border"}`}>
                    <button type="button" onClick={() => applySel(s)} className={`h-full pl-[11px] ${filter ? "pr-1" : "pr-[11px]"} text-[12.5px] font-semibold flex items-center gap-1.5`} data-testid="saved-selection"><Star size={12} />{s.name}<span className="opacity-60 font-medium">{s.user_ids.length}</span></button>
                    {filter && onDeleteSelection && <button type="button" onClick={() => onDeleteSelection(s.id)} title="Smazat výběr" aria-label={`Smazat výběr ${s.name}`} className="w-[26px] h-full opacity-60 hover:opacity-100 grid place-items-center"><X size={13} /></button>}
                  </span>
                );
              })}
              {!selections.length && naming === null && <span className="text-[12.5px] text-muted-foreground">Zatím žádný. Vyber lidi (třeba partu) a ulož si je.</span>}
              {canSave && <button type="button" onClick={() => setNaming("")} className="h-[30px] px-[11px] rounded-full border border-dashed border-border text-muted-foreground text-[12.5px] font-semibold" data-testid="save-selection">+ Uložit výběr</button>}
            </div>
            {naming !== null && (
              <div className="flex gap-1.5">
                <input autoFocus value={naming} onChange={(e) => setNaming(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveNow(); if (e.key === "Escape") setNaming(null); }}
                  placeholder="Název, např. Parta Dvořák" className="flex-1 min-w-0 h-[34px] px-3 border border-border rounded-[10px] bg-card text-[13.5px] outline-none" data-testid="selection-name" />
                <button type="button" onClick={saveNow} className="h-[34px] px-3.5 rounded-full bg-foreground text-background text-[13px] font-bold">Uložit</button>
                <button type="button" onClick={() => setNaming(null)} className="h-[34px] px-2.5 rounded-full text-muted-foreground text-[13px] font-semibold">Zrušit</button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="max-h-[360px] overflow-auto">
        {groups.map((g) => (
          <div key={g.role}>
            <div className="flex items-center gap-2.5 px-3.5 py-2 border-t border-border bg-secondary/60">
              <button type="button" onClick={() => toggleGroup(g.mem.map((p) => p.id))} title="Vybrat všechny na této pozici" aria-label={`Vybrat všechny: ${ROLE_LABEL[g.role] || g.role}`}><Box state={g.k === g.mem.length ? "all" : g.k ? "some" : null} /></button>
              <button type="button" onClick={() => setCollapsed((c) => (c.includes(g.role) ? c.filter((x) => x !== g.role) : [...c, g.role]))} className="flex-1 flex items-center gap-2 text-left">
                <span className="flex-1 text-[11.5px] font-bold tracking-[.08em] uppercase text-muted-foreground">{ROLE_LABEL[g.role] || g.role}</span>
                <span className="text-xs font-semibold text-muted-foreground">{g.k ? `${g.k} z ${g.mem.length}` : g.mem.length}</span>
                {g.open ? <ChevronDown size={13} className="text-muted-foreground" /> : <ChevronRight size={13} className="text-muted-foreground" />}
              </button>
            </div>
            {g.open && g.rows.map(({ p, a, abs }) => (
              <button type="button" key={p.id} onClick={() => toggle(p.id)} className={`w-full text-left flex items-center gap-2.5 px-3.5 py-[7px] hover:bg-secondary ${sel.has(p.id) ? "bg-brand/10" : ""}`} data-testid={`person-row-${p.id}`}>
                <Box state={sel.has(p.id) ? "all" : null} />
                <PersonDot person={p} size={26} />
                <div className="flex-1 min-w-0"><div className="text-[13.5px] font-semibold truncate">{p.name}{p.id === meId ? " (já)" : ""}</div><div className="text-[11.5px] text-muted-foreground">{ROLE_LABEL[p.role] || p.role}</div></div>
                {a && <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${pillCls(a.tone)}`}>{a.label}</span>}
                {!a && abs && <span className={`shrink-0 text-[10.5px] font-bold px-[7px] py-0.5 rounded-full ${TONE[absenceTone(abs)]?.tag}`}>{absenceLabel(abs).toLowerCase()}</span>}
              </button>
            ))}
          </div>
        ))}
        {!!q && !people.some(hit) && <div className="py-7 text-center text-[13px] text-muted-foreground">Nikdo takový v týmu není.</div>}
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border">
        <span className="text-[12.5px] font-semibold text-muted-foreground">{all ? (filter ? `Všichni · ${people.length} lidí` : "Nikdo nevybrán") : `Vybráno ${value.length} z ${people.length}`}</span>
        <button type="button" onClick={() => onChange([])} className="ml-auto h-8 px-3 rounded-full text-muted-foreground text-[13px] font-semibold hover:bg-secondary">Zrušit výběr</button>
        <button type="button" onClick={close} className="h-8 px-4 rounded-full bg-brand text-white text-[13px] font-bold" data-testid="assign-people-done">Hotovo</button>
      </div>
    </div>
  );

  const trigger = (
        <div className="relative max-md:w-full">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${mode}-people-trigger`}
            className="h-11 pl-2 pr-3.5 rounded-full border border-border bg-card flex items-center gap-2.5 text-sm font-semibold hover:bg-secondary max-w-full max-md:w-full">
            <span className="flex pl-1.5">
              {stackSrc.slice(0, filter ? 3 : 4).map((p) => <span key={p.id} className="-ml-[7px] rounded-full border-2 border-card"><PersonDot person={p} size={26} /></span>)}
              {stackSrc.length > (filter ? 3 : 4) && <span className="-ml-[7px] h-[30px] min-w-[30px] px-1.5 rounded-full border-2 border-card bg-secondary text-muted-foreground text-[10.5px] font-bold grid place-items-center">+{stackSrc.length - (filter ? 3 : 4)}</span>}
              {!stackSrc.length && <span className="w-7 h-7 rounded-full border-[1.5px] border-dashed border-border text-muted-foreground grid place-items-center">+</span>}
            </span>
            <span className="truncate max-md:flex-1 max-md:text-left">{label}</span>{filter && !all && <span className="font-medium text-muted-foreground whitespace-nowrap">z {people.length}</span>}
            <ChevronDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </div>
  );
  const tokens = !all ? (

        <div className="flex items-center gap-2 flex-wrap">
          {filter && <span className="text-[11px] font-bold tracking-[.12em] text-muted-foreground mr-0.5">VYBRÁNO</span>}
          {(filter ? selected.slice(0, 5) : selected).map((p, i) => {
            const a = availability ? availability(p) : null;
            return (
              <span key={p.id} className="flex items-center gap-1.5 h-8 pl-[3px] pr-1 rounded-full bg-brand/10 text-brand text-[13px] font-semibold max-w-full" data-testid={`token-${p.id}`}>
                <PersonDot person={p} size={26} /><span className="truncate">{p.id === meId ? "Já" : p.name}</span>
                {!filter && i === 0 && selected.length > 1 && <span className="text-[10.5px] font-extrabold px-[7px] py-0.5 rounded-full bg-brand text-white">odpovídá</span>}
                {a && a.rank > 0 && <span className={`text-[10.5px] font-bold px-[7px] py-0.5 rounded-full whitespace-nowrap ${pillCls(a.tone)}`}>{a.label}</span>}
                <button type="button" onClick={() => toggle(p.id)} title="Odebrat" aria-label={`Odebrat ${p.name}`} className="w-[22px] h-[22px] rounded-full grid place-items-center hover:bg-brand/20"><X size={13} /></button>
              </span>
            );
          })}
          {filter && selected.length > 5 && <span className="text-[13px] font-semibold text-muted-foreground">+{selected.length - 5} další</span>}
          <button type="button" onClick={() => onChange([])} className="h-8 px-3 rounded-full border border-border bg-card text-muted-foreground text-[13px] font-semibold hover:text-foreground" data-testid="clear-people">× Odebrat vše</button>
        </div>
  ) : null;
  if (part === "trigger") return trigger;
  if (part === "panel") return filter ? filterPanel : null;
  if (part === "tokens") return tokens;

  return (
    <div className="flex flex-col gap-2.5 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {trigger}
      </div>
      {open && (filter ? filterPanel : panel)}
      {tokens}
      {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

// 359: Úkoly › Filtr › Kdo ve stejném okně jako ostatní pole (dřív velký samostatný panel): hledání,
// rychlé volby, uložené výběry lidí (Stavbyvedoucí, Dělníci…) a lidé po rolích se zaškrtnutím celé role.
// ↑ ↓ vybírá řádek, Enter zaškrtne, Backspace v prázdném hledání = zpět na pole. Prázdný výběr = všichni.
const cls = (on) => `w-[18px] h-[18px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 text-[12px] font-extrabold ${on ? "bg-brand border-brand text-white" : "border-muted-foreground/60"}`;
export function PeopleFilterStep({ people, value, onChange, selections = [], onCreateSelection, onDeleteSelection, meId, absentToday = () => null,
  mobile, onBack, onDone, applyLabel }) {
  const [q, setQ] = useState("");
  const [act, setAct] = useState(0);
  const [naming, setNaming] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { if (!mobile) inputRef.current?.focus(); }, [mobile]);
  const sel = useMemo(() => new Set(value), [value]);
  const same = (ids) => ids.length === value.length && ids.every((i) => sel.has(i));
  const toggle = (id) => onChange(sel.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  const toggleGroup = (ids) => { const every = ids.every((i) => sel.has(i)); onChange(every ? value.filter((x) => !ids.includes(x)) : [...new Set([...value, ...ids])]); };
  const pickIds = (ids) => onChange(same(ids) ? [] : ids);
  const hit = (p) => !q || norm(`${p.name} ${ROLE_LABEL[p.role] || ""}`).includes(norm(q));
  const roles = [...ROLE_ORDER, ...new Set(people.map((p) => p.role).filter((r) => !ROLE_ORDER.includes(r)))];
  const groups = roles.map((role) => ({ role, mem: people.filter((p) => p.role === role) })).map((g) => ({ ...g, shown: g.mem.filter(hit) })).filter((g) => g.shown.length);
  const rows = groups.flatMap((g) => g.shown);
  const inWork = people.filter((p) => !absentToday(p)).map((p) => p.id);
  const away = people.filter((p) => absentToday(p)).map((p) => p.id);
  const canSave = !!onCreateSelection && value.length > 1 && !selections.some((s) => same(s.user_ids)) && naming === null;
  const saveNow = async () => { await onCreateSelection?.((naming || "").trim() || `Můj výběr ${selections.length + 1}`, value); setNaming(null); };
  useEffect(() => { listRef.current?.querySelector('[data-cur="true"]')?.scrollIntoView?.({ block: "nearest" }); }, [act]);
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAct((a) => Math.min(rows.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAct((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (rows[act]) toggle(rows[act].id); }
    else if (e.key === "Backspace" && !q) { e.preventDefault(); onBack?.(); }
  };
  const chip = (on) => `h-8 px-3 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 border transition-colors ${on ? "bg-brand border-brand text-white" : "bg-card border-border hover:border-brand/50"}`;
  const label = "font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground";
  const row = mobile ? "min-h-[52px] text-[15.5px]" : "min-h-[40px] text-sm";
  return (
    <div className="flex flex-col min-h-0" data-testid="filter-people-step">
      <div className="mx-3 mb-2 h-10 rounded-[12px] bg-secondary flex items-center gap-2 px-3 shrink-0">
        <Search size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setAct(0); }} onKeyDown={onKey} data-testid="people-search"
          placeholder="Hledat jméno nebo pozici…" aria-label="Hledat člověka" className="bg-transparent focus:outline-none w-full min-w-0 text-base md:text-sm" />
      </div>
      {!q && (
        <div className="px-3 pb-2 space-y-2.5 shrink-0">
          <div className="space-y-1.5">
            <div className={label}>Rychle</div>
            <div className="flex flex-wrap gap-1.5">
              {[["Jen já", [meId]], ["Dnes v práci", inWork], ["Dnes chybí", away]].map(([l, ids]) => {
                const on = ids.length > 0 && same(ids);
                return (
                  <button key={l} type="button" disabled={!ids.length} onClick={() => pickIds(ids)} aria-pressed={on} className={`${chip(on)} disabled:opacity-45`} data-testid={`pp-quick-${l}`}>
                    {l}<span className={`tabular-nums ${on ? "" : "text-muted-foreground"}`}>{ids.length}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className={label}>Moje výběry</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {selections.map((s) => {
                const on = same(s.user_ids);
                return (
                  <span key={s.id} className={`${chip(on)} !px-0 overflow-hidden`}>
                    <button type="button" onClick={() => pickIds(s.user_ids)} aria-pressed={on} className="h-full pl-3 pr-1 inline-flex items-center gap-1.5" data-testid="saved-selection">
                      <Star size={13} />{s.name}<span className={`tabular-nums ${on ? "" : "text-muted-foreground"}`}>{s.user_ids.length}</span>
                    </button>
                    {onDeleteSelection && <button type="button" onClick={() => onDeleteSelection(s.id)} title="Smazat výběr" aria-label={`Smazat výběr ${s.name}`} className="w-7 h-full grid place-items-center opacity-60 hover:opacity-100"><X size={13} /></button>}
                  </span>
                );
              })}
              {!selections.length && <span className="text-[13px] text-muted-foreground">Zatím žádný — vyber lidi a ulož si je.</span>}
            </div>
          </div>
        </div>
      )}
      <ul ref={listRef} className={`overflow-y-auto px-1.5 pb-2 ${mobile ? "" : "max-h-[300px]"}`} role="listbox" aria-multiselectable="true" aria-label="Lidé">
        {groups.map((g) => {
          const k = g.mem.filter((p) => sel.has(p.id)).length;
          return [
            <li key={`r-${g.role}`}>
              <button type="button" onClick={() => toggleGroup(g.mem.map((p) => p.id))} aria-label={`Vybrat všechny: ${ROLE_LABEL[g.role] || g.role}`} data-testid={`pp-role-${g.role}`}
                className="w-full flex items-center gap-2.5 px-2.5 min-h-[34px] rounded-[10px] text-left hover:bg-secondary">
                <span className={cls(k > 0)}>{k === g.mem.length ? <Check size={13} strokeWidth={3} /> : k ? "–" : null}</span>
                <span className={`${label} flex-1`}>{ROLE_LABEL[g.role] || g.role}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{k ? `${k} z ${g.mem.length}` : g.mem.length}</span>
              </button>
            </li>,
            ...g.shown.map((p) => {
              const i = rows.indexOf(p), on = sel.has(p.id), abs = absentToday(p);
              return (
                <li key={p.id}>
                  <button type="button" role="option" aria-selected={on} data-cur={i === act} onMouseEnter={() => setAct(i)} onClick={() => toggle(p.id)} data-testid={`person-row-${p.id}`}
                    className={`w-full flex items-center gap-2.5 pl-5 pr-2.5 rounded-[10px] text-left ${row} ${i === act ? "bg-secondary" : ""}`}>
                    <span className={cls(on)}>{on && <Check size={13} strokeWidth={3} />}</span>
                    <PersonDot person={p} size={24} />
                    <span className="flex-1 min-w-0 truncate">{p.name}{p.id === meId ? " (já)" : ""}</span>
                    {abs && <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${TONE[absenceTone(abs)]?.tag}`}>{absenceLabel(abs).toLowerCase()}</span>}
                  </button>
                </li>
              );
            }),
          ];
        })}
        {!rows.length && <li className="px-3 py-3 text-sm text-muted-foreground">Nikdo takový v týmu není.</li>}
      </ul>
      <div className="flex items-center gap-2 border-t border-border px-3 pt-2.5 pb-3 shrink-0">
        {naming !== null ? (
          <>
            <input autoFocus value={naming} onChange={(e) => setNaming(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveNow(); if (e.key === "Escape") { e.stopPropagation(); setNaming(null); } }}
              placeholder="Název, např. Parta Dvořák" className="flex-1 min-w-0 h-9 px-3 border border-border rounded-full bg-card text-base md:text-sm outline-none" data-testid="selection-name" />
            <Button onClick={saveNow} className="h-9 rounded-full font-bold">Uložit</Button>
          </>
        ) : (
          <>
            {canSave && <button type="button" onClick={() => setNaming("")} className="text-[13px] font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="save-selection"><Star size={14} />Uložit výběr</button>}
            <span className="flex-1" />
            {value.length > 0 && <button type="button" onClick={() => onChange([])} className="text-[13px] font-semibold text-muted-foreground hover:text-destructive" data-testid="pp-clear">Zrušit</button>}
            <Button onClick={onDone} className="h-9 rounded-full font-bold" data-testid="pp-apply">{applyLabel || "Hotovo"}</Button>
          </>
        )}
      </div>
    </div>
  );
}
